//eslint-disable react-hooks/exhaustive-deps
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import {
  ChevronDownIcon,
  Bars3BottomLeftIcon,
  BuildingOfficeIcon,
  TagIcon,
  BriefcaseIcon,
  ArrowTopRightOnSquareIcon,
  BuildingStorefrontIcon,
  DocumentChartBarIcon,
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

import AppContext from '../../contexts/AppContext';
import NAV_CONFIG from './Navconfig';
import { useVenueStore } from '../../pages/TsVenueLanding/TsVenueLanding';
import { buildCompendiumUrl } from '../../utils/venueUrl';
import usePushEnabled from '../../utils/usePushEnabled';

export function computeOnlineStatus(validity) {
  if (!validity?.lastUpdated) {
    return { label: 'Not Enabled', color: '#6B7280', hoursDiff: null, rank: 3 };
  }

  const latestMs = new Date(validity.lastUpdated).getTime();
  if (!Number.isFinite(latestMs)) {
    return { label: 'Not Enabled', color: '#6B7280', hoursDiff: null, rank: 3 };
  }

  const diffHours = (Date.now() - latestMs) / 36e5;

  if (diffHours <= 6) {
    return { label: 'Online', color: '#16A34A', hoursDiff: diffHours, rank: 0 };
  }
  if (diffHours < 12) {
    return { label: 'Warning', color: '#F59E0B', hoursDiff: diffHours, rank: 1 };
  }
  return { label: 'Offline', color: '#EF4444', hoursDiff: diffHours, rank: 2 };
}

export function formatSince(hoursDiff) {
  if (!Number.isFinite(hoursDiff)) return '';
  const h = Math.floor(hoursDiff);
  const m = Math.round((hoursDiff - h) * 60);
  return `${h}h ${m}m ago`;
}

const useIsActive = (path) => {
  const { pathname } = useLocation();
  if (!path) return false;
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
};

const findTopParentTitle = (items, pathname) => {
  for (const item of items) {
    if (item.children?.length) {
      if (item.children.some((child) => pathname === child.path || pathname.startsWith(`${child.path}/`))) {
        return item.title;
      }
      const deep = findTopParentTitle(item.children, pathname);
      if (deep) return deep;
    }
  }
  return null;
};

const filterByPermission = (items, userPerms, isMaster) =>
  items
    .filter((item) => {
      if (item.isHeading) return true;
      if (isMaster || !item.perm) return true;
      return Array.isArray(item.perm)
        ? item.perm.some((perm) => userPerms.includes(perm))
        : userPerms.includes(item.perm);
    })
    .map((item) =>
      item.children
        ? { ...item, children: filterByPermission(item.children, userPerms, isMaster) }
        : item
    );

const RoleBadge = ({ type }) => {
  if (type === 'venue') return <BuildingOfficeIcon className="h-5 w-5 text-[#4a63ff] flex-none" />;
  if (type === 'adv') return <TagIcon className="h-5 w-5 text-[#4a63ff] flex-none" />;
  if (type === 'tsVenue') return <BuildingStorefrontIcon className="h-5 w-5 text-[#4a63ff] flex-none" />;
  if (type === 'tsAdv') return <DocumentChartBarIcon className="h-5 w-5 text-[#4a63ff] flex-none" />;
  if (type === 'tsContent') return <ClipboardDocumentListIcon className="h-5 w-5 text-[#4a63ff] flex-none" />;
  return <BriefcaseIcon className="h-5 w-5 text-[#4a63ff] flex-none" />;
};

const resolveConfigPaths = (items, roleId) =>
  (items || []).map((item) => ({
    ...item,
    path: typeof item.path === 'string'
      ? item.path.replace(':roleId', roleId || '')
      : item.path,
    children: item.children ? resolveConfigPaths(item.children, roleId) : item.children,
  }));

const NavItem = ({ item, role, depth = 0, openTop, setOpenTop, navigate, onNavigate }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const isActive = useIsActive(item.path);

  if (item.isHeading) {
    return (
      <li className="cms-eyebrow px-2.5 pt-3 text-[#7f8aa8]">
        {item.title}
      </li>
    );
  }

  const Icon = item.icon;
  const isLeaf = !item.children?.length;
  const isTop = depth === 0;
  const isOpen = isTop ? openTop === item.title : localOpen;

  const baseButtonClasses =
    'group flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 cms-sidebar-text transition-all duration-200';
  const activeClasses =
    'bg-[linear-gradient(135deg,#f4f6ff_0%,#fbfcff_100%)] text-[#2b3d92] shadow-[0_8px_18px_rgba(74,92,255,0.06),inset_0_0_0_1px_rgba(74,92,255,0.12)]';
  const idleClasses = 'text-[#334154] hover:-translate-y-0.5 hover:bg-[#f8faff] hover:text-[#172033] hover:shadow-[0_8px_18px_rgba(15,23,42,0.045)]';

  if (isLeaf) {
    return (
      <li>
        <Link
          to={item.path}
          className={`${baseButtonClasses} ${isActive ? activeClasses : idleClasses}`}
          onClick={onNavigate}
        >
          {Icon && (
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg transition-all duration-200 group-hover:scale-105 ${isActive ? 'bg-white text-[#4a63ff]' : 'bg-[#eef2ff] text-[#4a63ff] group-hover:bg-white'}`}>
              <Icon className="h-3.5 w-3.5 shrink-0" />
            </span>
          )}
          <span className="cms-sidebar-text truncate">{item.title}</span>
          {isActive && <span className="ml-auto inline-block h-1.5 w-1.5 rounded-full bg-[#4a5cff]" />}
        </Link>
      </li>
    );
  }

  const toggleOpen = () => {
    if (isTop) {
      const willOpen = openTop !== item.title;
      setOpenTop(willOpen ? item.title : null);
      if (willOpen && item.children[0]?.path) {
        navigate(item.children[0].path);
        if (typeof onNavigate === 'function') onNavigate();
      }
    } else {
      setLocalOpen((open) => !open);
    }
  };

  return (
    <li>
      <button
        type="button"
        className={`${baseButtonClasses} ${isOpen ? activeClasses : idleClasses}`}
        onClick={toggleOpen}
      >
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg transition-all duration-200 group-hover:scale-105 ${isOpen ? 'bg-white text-[#4a63ff]' : 'bg-[#eef2ff] text-[#4a63ff] group-hover:bg-white'}`}>
          {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
        </span>
        <span className="cms-sidebar-text truncate">{item.title}</span>
        <ChevronDownIcon
          className={`ml-auto h-3.5 w-3.5 text-slate-400 transition-all duration-300 ease-out ${
            isOpen ? 'rotate-0 text-[#6f7b9b]' : '-rotate-90 group-hover:translate-x-0.5'
          }`}
        />
      </button>

      <div
        className={`grid transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isOpen ? 'mt-1.5 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <ul
            className={`ml-6 space-y-0.5 border-l border-[#dfe5f7] pl-2.5 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isOpen ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
            }`}
          >
            {item.children.map((child) => (
              <NavItem
                key={child.title}
                item={child}
                role={role}
                depth={depth + 1}
                openTop={openTop}
                setOpenTop={setOpenTop}
                navigate={navigate}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
};

const SideNavigationBar = () => {
  const {
    token,
    userInfo,
    selectedRole,
    setSelectedRole,
    isMasterUser,
    logout,
    subscriptionGate,
    isImpersonatedSession,
    impersonationTargetRole,
    impersonationSessionInvalid,
    importedEventSupportedVenues,
  } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredRole, setHoveredRole] = useState(selectedRole.id);
  const [openTop, setOpenTop] = useState(
    findTopParentTitle(resolveConfigPaths(NAV_CONFIG[selectedRole?.type || 'venue'] || [], selectedRole?.id), location.pathname)
  );

  const pushEnabled = usePushEnabled(selectedRole?.id, selectedRole?.type);
  const validity = useVenueStore((state) => state.validity);
  const status = computeOnlineStatus(validity);

  useEffect(() => {
    if (isImpersonatedSession) {
      return;
    }

    if (
      userInfo?.roleInfo &&
      (!selectedRole.id || !selectedRole.type) &&
      typeof setSelectedRole === 'function'
    ) {
      if (userInfo.user?.permissionLevel === 9) {
        logout();
        return;
      }

      const firstVenueRole = (userInfo.roleInfo.venue || [])[0];
      if (firstVenueRole) {
        setSelectedRole({
          id: firstVenueRole.id,
          type: 'venue',
          permissionLevel: firstVenueRole.permissionLevel,
        });
        return;
      }

      const firstAdvRole = (userInfo.roleInfo.adv || [])[0];
      if (firstAdvRole) {
        setSelectedRole({
          id: firstAdvRole.id,
          type: 'adv',
          permissionLevel: firstAdvRole.permissionLevel,
        });
        return;
      }

      const firstTsVenueRole = (userInfo.roleInfo.tsVenue || [])[0];
      if (firstTsVenueRole) {
        setSelectedRole({
          id: firstTsVenueRole.id,
          type: 'tsVenue',
          permissionLevel: firstTsVenueRole.permissionLevel,
        });
        return;
      }

      const firstTsAdvRole = (userInfo.roleInfo.tsAdv || [])[0];
      if (firstTsAdvRole) {
        setSelectedRole({
          id: firstTsAdvRole.id,
          type: 'tsAdv',
          permissionLevel: firstTsAdvRole.permissionLevel,
        });
      }
    }
  }, [isImpersonatedSession, logout, selectedRole, setSelectedRole, userInfo]);

  useEffect(() => {
    setOpenTop(findTopParentTitle(resolveConfigPaths(NAV_CONFIG[selectedRole?.type || 'venue'] || [], selectedRole?.id), location.pathname));
  }, [location.pathname, selectedRole?.id, selectedRole?.type]);

  useEffect(() => {
    if (selectedRole?.type !== 'tsVenue' || !selectedRole?.id || !token) {
      useVenueStore.getState().setValidity(null);
      return;
    }

    let isActive = true;

    const fetchValidity = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/ts/${selectedRole.id}/basic-info`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (isActive) {
          useVenueStore.getState().setValidity(data?.validity?.statusLogs?.[0] || null);
        }
      } catch (error) {
        console.error('Failed to fetch venue connectivity status:', error);
      }
    };

    fetchValidity();

    return () => {
      isActive = false;
    };
  }, [selectedRole?.id, selectedRole?.type, token]);

  const venueId = selectedRole.id || '';
  const dynamicURL = buildCompendiumUrl(venueId);
  const tsUrl = `https://${venueId}.touchandexplore.com`;

  const roleName = (roleId, type) => {
    const role = userInfo?.roleInfo?.[type]?.find((item) => item.id === roleId);
    if (role?.alias || role?.name) {
      return role.alias || role.name;
    }
    if (isImpersonatedSession) {
      return 'Session invalid - please re-enter from admin';
    }
    return 'Unknown';
  };

  const handleRoleChange = (roleId, type, permissionLevel) => {
    if (isImpersonatedSession && impersonationTargetRole) {
      const isLockedRole = roleId === impersonationTargetRole.id && type === impersonationTargetRole.type;
      if (!isLockedRole) {
        setIsDropdownOpen(false);
        return;
      }
    }

    setSelectedRole({ id: roleId, type, permissionLevel });
    setIsDropdownOpen(false);
    navigate('/');
  };

  const dropdownRoleItems = isImpersonatedSession
    ? (() => {
        if (!impersonationTargetRole) return [];
        const matchedRole = userInfo?.roleInfo?.[impersonationTargetRole.type]?.find(
          (item) => item.id === impersonationTargetRole.id
        );
        return [{
          id: impersonationTargetRole.id,
          permissionLevel: matchedRole?.permissionLevel ?? selectedRole?.permissionLevel,
          alias: matchedRole?.alias,
          name: matchedRole?.name,
          __type: impersonationTargetRole.type,
        }];
      })()
    : ['venue', 'adv', 'tsVenue', 'tsAdv'].flatMap((type) =>
        (userInfo?.roleInfo?.[type] || []).map((item) => ({ ...item, __type: type }))
      ).concat(
        (userInfo?.roleInfo?.tsContent || []).map((item) => ({ ...item, __type: 'tsContent' }))
      );

  const renderedConfig = useMemo(() => {
    const base = resolveConfigPaths(NAV_CONFIG[selectedRole?.type || 'venue'] || [], selectedRole?.id);
    const perms = userInfo?.permissions || [];
    const canManageFollowers = !userInfo?.user?.managedBy;
    const stripAddUsersForFollowers = (items = []) => items
      .map((item) => ({
        ...item,
        children: item.children ? stripAddUsersForFollowers(item.children) : item.children,
      }))
      .filter((item) => {
        if (!canManageFollowers && item.path === '/user/add-user') {
          return false;
        }
        if (Array.isArray(item.children) && item.children.length === 0 && !item.path) {
          return false;
        }
        return true;
      });
    let permittedItems = filterByPermission(base, perms, isMasterUser);
    permittedItems = stripAddUsersForFollowers(permittedItems);

    if (selectedRole?.type === 'tsVenue') {
      const isImportedEventVenue = (importedEventSupportedVenues || []).some(
        (venue) => venue.venueId === selectedRole?.id
      );
      if (!isImportedEventVenue) {
        permittedItems = permittedItems.filter((item) => item.path !== 'ts/venue/imported-events');
      }
    }

    // Guest notifications are only offered to venues the server has switched them on for.
    if (pushEnabled !== true) {
      permittedItems = permittedItems.filter((item) => item.path !== '/notifications');
    }

    if (subscriptionGate?.isBlocked && subscriptionGate?.billingOnly && selectedRole?.type === 'venue') {
      return permittedItems.filter((item) => item.path === '/billing');
    }
    return permittedItems;
  }, [
    selectedRole?.id,
    selectedRole?.type,
    subscriptionGate?.billingOnly,
    subscriptionGate?.isBlocked,
    userInfo?.permissions,
    isMasterUser,
    importedEventSupportedVenues,
    userInfo?.user?.managedBy,
    pushEnabled,
  ]);

  const currentVenue = userInfo?.roleInfo?.venue?.find((venue) => venue.id === selectedRole?.id);
  const isActive = currentVenue?.validity?.isActive ?? false;

  return (
    <>
      {!isNavOpen && (
        <button
          type="button"
          className="fixed left-3 top-4 z-50 inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#dbe2ff] bg-white text-slate-700 shadow-[0_14px_28px_rgba(15,23,42,0.12)] lg:hidden"
          onClick={() => setIsNavOpen(true)}
        >
          <Bars3BottomLeftIcon className="h-6 w-6" />
        </button>
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex min-w-64 max-w-64 flex-col border-r border-[#e6ebf7] bg-[linear-gradient(180deg,#ffffff_0%,#fcfdff_32%,#f7f9fd_100%)] shadow-[0_18px_42px_rgba(15,23,42,0.05)] transition-transform ${
          isNavOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:static lg:inset-auto lg:translate-x-0`}
      >
        <div className="border-b border-[#ebeff8] px-4 pt-3 pb-4">
          <div className="mb-2 flex items-center justify-center">
            <img
              src={`${process.env.PUBLIC_URL}/assets/Platypus360_Logo.png`}
              alt="Platypus Logo"
              className="h-11 object-contain"
            />
          </div>

          {(userInfo?.user?.permissionLevel !== 9 || isImpersonatedSession) && (
            <div className="relative border-t border-[#ebeff8] pt-1">
              <button
                type="button"
                className="flex w-full items-center gap-2.5 rounded-[18px] border border-transparent bg-transparent px-2 py-2 text-left shadow-none transition-all duration-200 hover:bg-[#f8faff]"
                onClick={() => setIsDropdownOpen((open) => !open)}
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#eef2ff]">
                  <RoleBadge type={selectedRole?.type} />
                </span>
                <span className="min-w-0 flex-1 pt-1">
                  <span className="cms-eyebrow block truncate text-[#7f8aa8]">
                    Active Role
                  </span>
                  <span className="cms-sidebar-text mt-[1px] block truncate text-[#1d293d]">
                    {roleName(selectedRole?.id, selectedRole?.type)}
                  </span>
                </span>
                <ChevronDownIcon
                  className={`h-3.5 w-3.5 text-[#97a1bd] transition-all duration-300 ease-out ${
                    isDropdownOpen ? 'rotate-180 text-[#6c7895]' : 'rotate-0'
                  }`}
                />
              </button>

              {impersonationSessionInvalid && (
                <div className="mt-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-700">
                  <div className="font-medium">Session invalid - please re-enter from admin.</div>
                  <button
                    type="button"
                    className="mt-2 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-rose-700 shadow-[inset_0_0_0_1px_rgba(190,24,93,0.15)]"
                    onClick={logout}
                  >
                    Back to login
                  </button>
                </div>
              )}

              {isDropdownOpen && (
                <div className="absolute -left-2 -right-2 top-full z-50 mt-2 max-h-[640px] overflow-y-auto rounded-[20px] border border-[#dfe5f6] bg-white shadow-[0_18px_32px_rgba(15,23,42,0.08)]">
                  {dropdownRoleItems.map((item) => (
                    <button
                      key={`${item.__type}-${item.id}`}
                      type="button"
                      className={`cms-sidebar-text flex w-full items-center gap-2.5 px-3 py-2.5 transition-all duration-200 ${
                        hoveredRole === item.id ? 'bg-[#f5f7ff] shadow-[inset_0_0_0_1px_rgba(74,92,255,0.08)]' : 'bg-white'
                      }`}
                      onMouseEnter={() => setHoveredRole(item.id)}
                      onMouseLeave={() => setHoveredRole(null)}
                      onClick={() => handleRoleChange(item.id, item.__type, item.permissionLevel)}
                    >
                      <RoleBadge type={item.__type} />
                      <span className="truncate text-left font-medium text-[#243145]">
                        {roleName(item.id, item.__type)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-2.5 py-3">
          <nav>
            <ul className="space-y-1">
              {renderedConfig.map((item) => (
                <NavItem
                  key={item.title}
                  item={item}
                  role={selectedRole.type}
                  openTop={openTop}
                  setOpenTop={setOpenTop}
                  navigate={navigate}
                  onNavigate={() => setIsNavOpen(false)}
                />
              ))}
            </ul>
          </nav>
        </div>

        <div className="border-t border-[#ebeff8] p-3.5">
          {selectedRole.type === 'tsVenue' && (
            <div className="mb-3 rounded-xl border border-[#dfe5f6] bg-white px-3 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.04)]">
              <div className="cms-eyebrow mb-1.5 text-[#7f8aa8]">
                Connectivity
              </div>
              <div className="flex items-center gap-1.5 text-[13px] font-medium text-[#243145]">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: status.color }}
                  title={status.label}
                />
                <span>{status.label}</span>
                <span className="text-[11px] text-[#7f8aa8]">{formatSince(status.hoursDiff)}</span>
              </div>
            </div>
          )}

        </div>

        {selectedRole.type === 'venue' && !(subscriptionGate?.isBlocked && subscriptionGate?.billingOnly) && (
          <div className="border-t border-[#ebeff8] px-4 py-3.5">
            <a
              href={dynamicURL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2.5 rounded-xl px-2.5 py-2 cms-sidebar-text text-[#334154] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#f7f8fd] hover:text-[#172033] hover:shadow-[0_10px_22px_rgba(15,23,42,0.06)]"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#eef2ff] text-[#4a63ff] transition-all duration-200 group-hover:scale-105 group-hover:bg-white">
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </span>
              <span className="truncate">View Compendium</span>
              {!isActive && (
                <span className="ml-2 flex items-center gap-1 text-xs font-bold text-rose-500">
                  SUSPENDED
                  <Tooltip
                    title="Your venue has been suspended. Please contact JBG for more information."
                    placement="top"
                    color="white"
                    overlayInnerStyle={{ color: '#333', fontSize: 12 }}
                  >
                    <QuestionMarkCircleIcon className="h-3.5 w-3.5 cursor-pointer text-rose-400" />
                  </Tooltip>
                </span>
              )}
            </a>
          </div>
        )}

        {selectedRole.type === 'tsVenue' && (
          <div className="border-t border-[#ebeff8] px-4 py-3.5">
            <a
              href={tsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2.5 rounded-xl px-2.5 py-2 cms-sidebar-text text-[#334154] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#f7f8fd] hover:text-[#172033] hover:shadow-[0_10px_22px_rgba(15,23,42,0.06)]"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#eef2ff] text-[#4a63ff] transition-all duration-200 group-hover:scale-105 group-hover:bg-white">
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </span>
              <span className="truncate">View Touchscreen</span>
            </a>
          </div>
        )}
      </aside>

      {impersonationSessionInvalid && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-3xl border border-rose-200 bg-white p-6 shadow-[0_24px_64px_rgba(15,23,42,0.22)]">
            <div className="text-lg font-semibold text-[#172033]">Session invalid</div>
            <p className="mt-2 text-sm leading-6 text-[#4b5565]">
              This impersonation session can no longer be verified. Please re-enter from admin to continue.
            </p>
            <button
              type="button"
              className="mt-5 inline-flex rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-rose-700"
              onClick={logout}
            >
              Back to login
            </button>
          </div>
        </div>
      )}

      {isNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-950/40 backdrop-blur-[2px] lg:hidden"
          onClick={() => setIsNavOpen(false)}
        />
      )}
    </>
  );
};

export default SideNavigationBar;
